import type { BoardAppointment, BoardStation } from './board-types'

const EMPTY_LABEL = '-'
const PAUSED_LABEL = 'перерыв'
const WAITING_CLIENT_LABEL = 'Ожидает клиента'
const CURRENT_CLIENT_CAPTION = 'Сейчас'

type CurrentClientCardTone = 'active' | 'called' | 'empty' | 'paused' | 'waiting'

const PAUSED_STATION_STATUSES = new Set(['break', 'paused'])
const WAITING_CLIENT_STATUS = 'busy'

const getSortValue = (value?: number) => value ?? Number.POSITIVE_INFINITY

const normalizeAppointmentDisplayName = (displayName: string) => {
    return displayName.replaceAll('#', '').replaceAll(/\s+/g, ' ').trim()
}

export const formatAppointmentLabel = (appointment?: BoardAppointment | null) => {
    if (!appointment) {
        return EMPTY_LABEL
    }

    const displayName = normalizeAppointmentDisplayName(
        appointment.display_name?.trim() ?? appointment.client_display_name?.trim() ?? ''
    )
    const emoji = appointment.emoji?.trim()
    const position = appointment.queue_position

    if (displayName) {
        return displayName
    }

    if (emoji && position !== undefined && position !== null) {
        return `${emoji} ${position}`
    }

    if (emoji) {
        return emoji
    }

    if (position !== undefined && position !== null) {
        return String(position)
    }

    return EMPTY_LABEL
}

export const sortStationsForBoard = (stations: BoardStation[] = []) => {
    return stations.toSorted((left, right) => {
        const orderDiff = getSortValue(left.display_order) - getSortValue(right.display_order)

        if (orderDiff !== 0) {
            return orderDiff
        }

        return getSortValue(left.station_id ?? left.id) - getSortValue(right.station_id ?? right.id)
    })
}

export const getStationSeatLabel = (station: BoardStation) => {
    const stationId = station.station_id ?? station.id

    return stationId ? String(stationId) : (station.station_name ?? station.name ?? EMPTY_LABEL)
}

export const getStationHaircutsLabel = (station: BoardStation) => {
    return String(station.total_haircuts ?? 0)
}

const isActiveCurrentClient = (station: BoardStation) => {
    const stationStatus = station.status?.toLowerCase()
    const appointmentStatus = station.current_client?.status?.toLowerCase()

    return (
        stationStatus === 'busy' ||
        Boolean(station.current_client?.actual_start_time) ||
        appointmentStatus === 'busy' ||
        appointmentStatus === 'active' ||
        appointmentStatus === 'in_progress'
    )
}

export const getCurrentClientCardView = (
    station: BoardStation
): {
    caption?: string
    isFallbackLabel?: boolean
    label: string
    tone: CurrentClientCardTone
} => {
    const stationStatus = station.status?.toLowerCase() ?? ''

    if (PAUSED_STATION_STATUSES.has(stationStatus)) {
        return {
            label: PAUSED_LABEL,
            tone: 'paused',
        }
    }

    if (station.current_client) {
        return {
            caption: CURRENT_CLIENT_CAPTION,
            label: formatAppointmentLabel(station.current_client),
            tone: isActiveCurrentClient(station) ? 'active' : 'called',
        }
    }

    if (stationStatus === WAITING_CLIENT_STATUS) {
        const waitingClient = station.queue?.[0]

        return {
            caption: CURRENT_CLIENT_CAPTION,
            isFallbackLabel: !waitingClient,
            label: waitingClient ? formatAppointmentLabel(waitingClient) : WAITING_CLIENT_LABEL,
            tone: 'waiting',
        }
    }

    return {
        label: EMPTY_LABEL,
        tone: 'empty',
    }
}

export const getStationStatusLabel = (status?: string) => {
    switch (status) {
        case 'busy': {
            return 'В работе'
        }
        case 'break': {
            return 'Пауза'
        }
        case 'free': {
            return 'Свободно'
        }
        default: {
            return status ?? 'Свободно'
        }
    }
}

export const getStationStatusTone = (station: BoardStation) => {
    if (station.current_client) {
        return 'active'
    }

    if (station.status === 'break') {
        return 'paused'
    }

    return 'idle'
}
